import React, {FC, useState, useEffect} from "react";
import {Select, DatePicker, Space, Checkbox} from "antd";
import moment, {Moment} from "moment";
import {grey} from "@ant-design/colors";
import {DateRangePreset, DateRange} from "../types";
import {DATE_RANGE_PRESET_OPTIONS, getDateRangeFromPreset, formatDateRange} from "../utils/dateRangePresets";
import {DATE_RANGE_FORMATS} from "../../../../views/DateInput/DateInput";

const {RangePicker} = DatePicker;

type DateRangeSelectorProps = {
    onDateRangeChange: (dateRange: DateRange) => void;
    onRespectFiltersChange: (respectFilters: boolean) => void;
    respectFilters: boolean;
    dateFieldDescription?: string;
    filterInfoText?: string;
    entityLabel?: string;
};

const DateRangeSelector: FC<DateRangeSelectorProps> = ({onDateRangeChange, onRespectFiltersChange, respectFilters, dateFieldDescription, filterInfoText, entityLabel = "Umsätze"}) => {
    const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>("currentMonth");
    const [customDateRange, setCustomDateRange] = useState<[Moment, Moment] | null>(null);
    const [currentDateRange, setCurrentDateRange] = useState<DateRange>(getDateRangeFromPreset("currentMonth"));

    useEffect(() => {
        onDateRangeChange(currentDateRange);
    }, [currentDateRange, onDateRangeChange]);

    const handlePresetChange = (value: DateRangePreset) => {
        setSelectedPreset(value);

        if (value === "custom") {
            // Wenn "Benutzerdefiniert" ausgewählt wird, verwende den aktuellen Monat als Basis
            const defaultRange = getDateRangeFromPreset("currentMonth");

            setCustomDateRange([defaultRange.start, defaultRange.end]);
            setCurrentDateRange(defaultRange);
        } else {
            const range = getDateRangeFromPreset(value);

            setCurrentDateRange(range);
            setCustomDateRange(null);
        }
    };

    const handleCustomRangeChange = (dates: [Moment | null, Moment | null] | null) => {
        if (dates && dates[0] && dates[1]) {
            setCustomDateRange([dates[0], dates[1]]);
            setCurrentDateRange({
                start: dates[0],
                end: dates[1],
                label: "Benutzerdefiniert",
            });
        }
    };

    return (
        <div
            style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 15,
            }}
        >
            <div>
                <div style={{fontWeight: "bold", marginBottom: 5}}>Zeitraum auswählen</div>
                <Select style={{width: "100%"}} value={selectedPreset} onChange={handlePresetChange} options={DATE_RANGE_PRESET_OPTIONS} />
            </div>

            {selectedPreset === "custom" && (
                <div>
                    <div style={{fontWeight: "bold", marginBottom: 5}}>Benutzerdefinierter Zeitraum</div>
                    <RangePicker style={{width: "100%"}} value={customDateRange} onChange={handleCustomRangeChange} format={DATE_RANGE_FORMATS} placeholder={["Von", "Bis"]} />
                </div>
            )}

            <div
                style={{
                    padding: 10,
                    // backgroundColor: grey[1],
                    border: `1px solid ${grey[1]}`,
                    borderRadius: 4,
                    textAlign: "center",
                }}
            >
                <div style={{fontSize: 13, color: grey[6], marginBottom: 5}}>Ausgewählter Zeitraum:</div>
                <div style={{fontWeight: "bold"}}>{formatDateRange(currentDateRange.start, currentDateRange.end)}</div>
                {dateFieldDescription && (
                    <div style={{fontSize: 12, color: grey[5], textAlign: "center", marginTop: 2}}>
                        {dateFieldDescription}
                    </div>
                )}
            </div>

            <Checkbox checked={respectFilters} onChange={(e) => onRespectFiltersChange(e.target.checked)}>
                Aktive Tabellenfilter berücksichtigen
            </Checkbox>

            <div style={{fontSize: 12, color: grey[5], textAlign: "center"}}>
                {filterInfoText
                    ? filterInfoText
                    : respectFilters
                    ? "Export berücksichtigt aktive Filter (z.B. Standort, Kursart) zusätzlich zum Zeitraum"
                    : `Export exportiert nur ${entityLabel} im ausgewählten Zeitraum, andere Filter werden ignoriert`}
            </div>
        </div>
    );
};

export default DateRangeSelector;
